import { useEffect, useRef, useState } from "react";
import { variantClasses } from "@/components/ui/text-variants";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/utils/utils";

interface Props {
  subject: string;
  className?: string;
}

// Dreamer-generated subject, clamped to two lines with an ellipsis. When the
// subject is clipped, hovering reveals the full text in a tooltip. Uses a raw
// <p> (not the Text component) so we can attach a ref for overflow detection —
// line-clamp overflows vertically, which Text's single-line check misses.
const DraftEmailSubject = ({ subject, className }: Props) => {
  const ref = useRef<HTMLParagraphElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const check = () =>
      setIsTruncated(element.scrollHeight > element.clientHeight + 1);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(element);
    return () => observer.disconnect();
  }, [subject]);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <p
          ref={ref}
          className={cn(
            variantClasses.lgBody,
            "line-clamp-2 min-w-0 cursor-default break-words font-medium",
            className
          )}
          data-test="draft-email-subject"
        >
          {subject}
        </p>
      </TooltipTrigger>
      {isTruncated ? (
        <TooltipContent maxWidth={24} enableMarkdown={false}>
          {subject}
        </TooltipContent>
      ) : null}
    </Tooltip>
  );
};

export default DraftEmailSubject;
